import { motion } from "framer-motion";
import { ArrowRight, Play, Zap, Shield, Brain, Mic, Activity } from "lucide-react";
import Button from "../ui/Button";
import Badge from "../ui/Badge";

const stats = [
  { value: "%95+", label: "Tanıma Doğruluğu", sublabel: "Tıbbi terim doğruluğu", color: "text-primary-500" },
  { value: "Anlık", label: "Konuşma → Rapor", sublabel: "Gerçek zamanlı dönüşüm", color: "text-emerald-500" },
  { value: "<2s", label: "Yanıt Süresi", sublabel: "Akıllı önbellekleme", color: "text-sky-500" },
  { value: "1,200", label: "Eşzamanlı Kullanıcı", sublabel: "1.8GB bellek", color: "text-violet-500" },
];

export default function Hero() {
  const scrollToContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToDemo = () => {
    document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white dark:bg-navy-900">
      {/* Mesh gradient background */}
      <div className="absolute inset-0 bg-mesh-hero" />

      {/* Animated blobs */}
      <div className="absolute top-20 -left-32 w-96 h-96 blob blob-primary" />
      <div className="absolute bottom-20 -right-32 w-[500px] h-[500px] blob blob-medical" />
      <div className="absolute top-1/2 left-1/3 w-72 h-72 blob blob-emerald" />

      {/* Floating particles */}
      <div className="particles-container">
        <div className="particle particle-1" />
        <div className="particle particle-2" />
        <div className="particle particle-3" />
        <div className="particle particle-4" />
        <div className="particle particle-5" />
      </div>

      {/* Dot grid overlay */}
      <div className="absolute inset-0 bg-dots opacity-40" />

      <div className="section-container relative z-10 pt-28 pb-16">
        <div className="text-center max-w-5xl mx-auto">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-block mb-8"
          >
            <Badge variant="medical" size="lg">
              <Zap size={16} className="mr-2" />
              Yapay Zeka Destekli Tıbbi Raporlama Platformu
            </Badge>
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="text-display-lg md:text-display-xl lg:text-display-2xl font-display font-bold mb-6 leading-tight"
          >
            <span className="text-gray-900 dark:text-white">
              Tıbbi Raporlamada{" "}
            </span>
            <br />
            <span className="text-gradient-premium">Yeni Nesil AI</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-lg md:text-xl text-gray-600 dark:text-gray-400 mb-10 max-w-3xl mx-auto leading-relaxed"
          >
            <strong className="text-gray-900 dark:text-white">MultiVision Report</strong> ile
            konuşmanızı gerçek zamanlı olarak yapılandırılmış tıbbi raporlara dönüştürün.{" "}
            <span className="text-primary-600 dark:text-primary-400 font-semibold">
              1,900 gerçek tıbbi rapor
            </span>{" "}
            üzerinde eğitilmiş, %100 tıbbi terim doğruluğu.
          </motion.p>

          {/* Feature Pills */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="flex flex-wrap items-center justify-center gap-3 mb-12"
          >
            {[
              { icon: Brain, text: "Akıllı Metin Analizi", color: "text-primary-600 dark:text-primary-400" },
              { icon: Mic, text: "Sesli Raporlama", color: "text-emerald-600 dark:text-emerald-400" },
              { icon: Shield, text: "KVKK & ISO 27001", color: "text-sky-600 dark:text-sky-400" },
              { icon: Activity, text: "Gerçek Zamanlı", color: "text-violet-600 dark:text-violet-400" },
            ].map((item) => (
              <div
                key={item.text}
                className="flex items-center space-x-2 px-4 py-2 rounded-full bg-white/60 dark:bg-white/5 backdrop-blur-sm border border-gray-200/60 dark:border-gray-700/40 transition-all duration-200 hover:border-primary-300 dark:hover:border-primary-600"
              >
                <item.icon className={item.color} size={16} />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {item.text}
                </span>
              </div>
            ))}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
          >
            <Button size="lg" onClick={scrollToContact}>
              Ücretsiz Demo Talep Et
              <ArrowRight size={20} />
            </Button>
            <Button variant="secondary" size="lg" onClick={scrollToDemo}>
              <Play size={20} />
              Nasıl Çalışır?
            </Button>
          </motion.div>

          {/* Stats Grid */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                className="relative group"
              >
                <div className="p-5 md:p-6 rounded-2xl bg-white/60 dark:bg-white/5 backdrop-blur-xl border border-white/40 dark:border-gray-700/40 hover:border-primary-200 dark:hover:border-primary-700/50 transition-all duration-300 hover:shadow-primary-soft">
                  <div className={`text-3xl md:text-4xl font-bold mb-1 ${stat.color}`}>
                    {stat.value}
                  </div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white mb-0.5">
                    {stat.label}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-500">
                    {stat.sublabel}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
